import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../types';
import notificationService from '../services/notificationService';
import NotificationStage from '../components/NotificationStage';

type PreviewType = 'donation' | 'challenge';

interface NotificationPreviewProps {}

const NotificationPreview: React.FC<NotificationPreviewProps> = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<PreviewType>('donation');
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark'>('dark');
  const [showPreview, setShowPreview] = useState(true);

  // Mock notification data with various text lengths to test overflow handling
  const mockNotifications: Record<PreviewType, Notification> = {
    donation: {
      id: 'mock-donation-1',
      created_at: new Date().toISOString(),
      description: 'The challenge you created received a new donation.',
      status: 'SENT',
      type_sender: 'user',
      sender_id: 'mock-user-1',
      sender_name: 'Alvaro',
      thumbnail_sender: '',
      target_screen: 'challenge',
      target_id: 'mock-challenge-1',
      target_name: 'Coolest Notification Name Ever',
      thumbnail_target: '',
      opp_id: 'mock-opp-1',
      notification_type: 'OPP_NEW_DONATION',
      coin_value: 500
    },
    challenge: {
      id: 'mock-challenge-1',
      created_at: new Date().toISOString(),
      description: 'Falcon1337 created a new challenge for you with a very long description that might overflow the text area and should be handled gracefully.',
      status: 'SENT',
      type_sender: 'user',
      sender_id: 'mock-user-2',
      sender_name: 'Falcon1337',
      thumbnail_sender: '',
      target_screen: 'challenge',
      target_id: 'mock-challenge-2',
      target_name: 'Epic Gaming Challenge',
      thumbnail_target: '',
      opp_id: 'mock-opp-2',
      notification_type: 'USER_TAGGED',
      coin_value: 10
    },
  };

  const currentNotification = mockNotifications[selectedType];
  const notificationSettings = notificationService.getSettings();

  useEffect(() => {
    const root = document.getElementById('root');
    document.documentElement.style.backgroundColor = 'transparent';
    document.body.style.backgroundColor = 'transparent';
    if (root) {
      root.style.backgroundColor = 'transparent';
    }

    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      if (root) {
        root.style.backgroundColor = '';
      }
    };
  }, []);

  const handleBack = () => {
    navigate('/settings');
  };

  const handleTypeChange = (type: PreviewType) => {
    setSelectedType(type);
    setShowPreview(false);
    setTimeout(() => setShowPreview(true), 100);
  };

  const handleThemeChange = (theme: 'light' | 'dark') => {
    setSelectedTheme(theme);
    setShowPreview(false);
    setTimeout(() => setShowPreview(true), 100);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'transparent',
        overflow: 'hidden',
      }}
    >
      {/* Header with controls */}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          right: 10,
          zIndex: 10002,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: 1,
          borderRadius: 1,
          pointerEvents: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Typography variant="h6" color="white" sx={{ fontFamily: 'Montserrat' }}>
            Notification Preview
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pointerEvents: 'auto' }}>
          <FormControl size="small" sx={{ minWidth: 120, zIndex: 10002 }}>
            <Select
              value={selectedType}
              onChange={(e) => handleTypeChange(e.target.value as PreviewType)}
              displayEmpty
              renderValue={(value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Type'}
              sx={{
                color: 'white',
                zIndex: 10002,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(95, 224, 227, 1)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(32, 46, 64, 0.95)',
                    color: 'white',
                    zIndex: 10003,
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(95, 224, 227, 0.3)',
                      },
                    },
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                disableScrollLock: true,
              }}
            >
              <MenuItem value="donation">Donation</MenuItem>
              <MenuItem value="challenge">Challenge</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120, zIndex: 10002 }}>
            <InputLabel sx={{ color: 'white' }}>Theme</InputLabel>
            <Select
              value={selectedTheme}
              onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark')}
              label="Theme"
              sx={{
                color: 'white',
                zIndex: 10002,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(95, 224, 227, 1)',
                },
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(32, 46, 64, 0.95)',
                    color: 'white',
                    zIndex: 10003,
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(95, 224, 227, 0.3)',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="light">Light</MenuItem>
            </Select>
          </FormControl>

        </Box>
      </Box>

      {/* Notification Alert Preview */}
      {showPreview && (
        <NotificationStage
          notification={currentNotification}
          onClose={() => {}}
          theme={selectedTheme}
          donationTextTemplate={notificationSettings.donationTextTemplate}
          challengeTextTemplate={notificationSettings.challengeTextTemplate}
          isPreview
        />
      )}
    </Box>
  );
};

export default NotificationPreview;
